import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MdHomeWork,
  MdTrendingUp,
  MdOutlineLayers,
  MdOutlineCurrencyExchange,
  MdChevronRight,
} from "react-icons/md";
import axiosInstance from "../../axios";
import {
  formatRupiah,
  formatNilaiListing,
  formatNilaiListingLengkap,
} from "../../utils/currency";

import { formatNumber, safeNumber } from "../../utils/number";

const DashboardSummary = () => {
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await axiosInstance.get("/properties/my");

        const raw = response?.data?.data;

        const list = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : [];

        if (!isMounted) {
          return;
        }

        setProperties(list);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setError(
          err?.response?.data?.message || "Gagal memuat ringkasan dashboard.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  const summary = useMemo(() => {
    const total = properties.length;

    const forSale = properties.filter(
      (property) => property?.status === "for_sale",
    ).length;

    const forRent = properties.filter(
      (property) => property?.status === "for_rent",
    ).length;

    const totalValue = properties.reduce((sum, property) => {
      return sum + safeNumber(property?.price);
    }, 0);

    return {
      total,
      forSale,
      forRent,
      totalValue,
    };
  }, [properties]);

  if (loading) {
    return (
      <div className="dashboard-state-card">
        <div className="dashboard-state-card__spinner" />
        <p>Memuat dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-alert dashboard-alert--error">{error}</div>
    );
  }

  const cards = [
    {
      label: "Total Properti",
      value: new Intl.NumberFormat("id-ID").format(summary.total),
      icon: MdHomeWork,
      tooltip: `${summary.total} Properti`,
    },
    {
      label: "Dijual",
      value: formatNumber(summary.forSale),
      icon: MdTrendingUp,
      tooltip: `${summary.forSale} Properti Dijual`,
    },
    {
      label: "Disewakan",
      value: formatNumber(summary.forRent),
      icon: MdOutlineLayers,
      tooltip: `${summary.forRent} Properti Disewakan`,
    },
    {
      label: "Total Nilai Listing",
      value: formatNilaiListing(summary.totalValue),
      tooltip: formatNilaiListingLengkap(summary.totalValue),
      icon: MdOutlineCurrencyExchange,
      isMoney: true,
    },
  ];

  return (
    <div className="dashboard-summary">
      <div className="dashboard-summary__grid">
        {cards.map((item) => {
          const Icon = item.icon;

          return (
            <article key={item.label} className="dashboard-summary-card">
              <div className="dashboard-summary-card__top">
                <div className="dashboard-summary-card__icon">
                  <Icon size={22} />
                </div>

                <div className="dashboard-summary-card__label">
                  {item.label}
                </div>
              </div>

              <div
                className={`dashboard-summary-card__value ${
                  item.isMoney ? "dashboard-summary-card__value--money" : ""
                }`}
                title={item.tooltip || String(item.value)}
              >
                {item.value}
              </div>
            </article>
          );
        })}
      </div>

      <div className="dashboard-summary__recent">
        <div className="dashboard-summary__recent-header">
          <h3>Properti Terbaru</h3>

          <Link
            to="/dashboard/properties"
            className="dashboard-summary__recent-link"
          >
            Lihat semua
            <MdChevronRight size={18} />
          </Link>
        </div>

        {properties.length === 0 ? (
          <div className="dashboard-summary__empty">Belum ada properti.</div>
        ) : (
          <div className="dashboard-summary__recent-list">
            {properties.slice(0, 5).map((property) => (
              <div key={property.id} className="dashboard-summary-item">
                <div className="dashboard-summary-item__main">
                  <div className="dashboard-summary-item__title">
                    {property.title || "-"}
                  </div>

                  <div className="dashboard-summary-item__meta">
                    {property.city || "-"} •{" "}
                    {property.status === "for_sale" ? "Dijual" : "Disewakan"}
                  </div>
                </div>

                <div className="dashboard-summary-item__price">
                  {property.price_format || formatRupiah(property.price)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardSummary;
